<script setup lang="ts">
import {
  STATE_CONTENT_ADDRESS_SYSTEM,
  STATE_MENU_ADDRESS_SYSTEM,
} from '@/constants/statesOfAddressSystem'

import {
  useAddressSystemStateStore,
  useAddressSystemStore,
} from '@/store/addressSystem'
import { useUserStore } from '@/store/user/user'

import {
  DeliveryMethods,
  SetDeliveryMethodPayload,
} from '@/api/modules/basket/types'

type Props = { deliveryBasket: SetDeliveryMethodPayload }

const props = defineProps<Props>()

const emit = defineEmits(['onChangeBasketAddress'])

const { isDesktop } = useDevice()
const { $metrics } = useNuxtApp()

const userStore = useUserStore()
const isAuthenticated = computed(() => userStore.isAuthenticated)

const onChangeBasketAddress = () => {
  emit('onChangeBasketAddress')

  const { delivery_method } = props.deliveryBasket

  // Нажата кнопка "добавить адрес" в попапе без регистрации
  if (delivery_method === DeliveryMethods.COURIER && !isAuthenticated.value) {
    $metrics?.eventSendGoal({ ym: 'auth_popup_add_address_clicked' })
  }
}

const { deliveryAddress, isLoadingWidget } = storeToRefs(
  useAddressSystemStore(),
)
const { dialogContent, menuState } = storeToRefs(useAddressSystemStateStore())

const { resetDeliveryAddress } = useAddressSystemStore()
const { changeDialogContent } = useAddressSystemStateStore()

const clickToAddNewAddress = () => {
  resetDeliveryAddress()

  changeDialogContent(STATE_CONTENT_ADDRESS_SYSTEM.NEW)

  $metrics?.eventSendGoal({ ym: 'add_address' })
}

const isDisabledAction = computed(() => {
  const { delivery_method, point_id, address_id } = props.deliveryBasket

  // Если текущее состояние ДО = выбор существующего адреса или метод доставки самовывоз/в ресторане, то проверям по id установленного адреса
  if (
    dialogContent.value === STATE_CONTENT_ADDRESS_SYSTEM.SELECT ||
    delivery_method !== DeliveryMethods.COURIER
  ) {
    return !point_id && !address_id
  }

  // Если текущее состояние ДО = выбор нового адреса и метод доставки курьер, то проверяем по выбранному адресу
  return (
    !deliveryAddress.value?.address?.address?.street_with_type ||
    !deliveryAddress.value?.address?.address?.house
  )
})

// В зависимости от интерфейса определяем параметры кнопок
const paramsOfButton = computed(() => {
  const { delivery_method } = props.deliveryBasket

  const isSelectContent =
    dialogContent.value === STATE_CONTENT_ADDRESS_SYSTEM.SELECT

  // Определяем параметры для кнопок ДО
  const paramsSecondButton = isSelectContent
    ? { title: 'Готово', width: isDesktop ? 260 : '' }
    : { title: 'Продолжить', width: isDesktop ? 350 : '' }

  const visibleAddAddress =
    isSelectContent && delivery_method === DeliveryMethods.COURIER

  const visibleShowMap =
    isSelectContent && delivery_method !== DeliveryMethods.COURIER && !isDesktop

  return {
    visibleAddAddress,
    visibleShowMap,
    ...paramsSecondButton,
  }
})
</script>

<template>
  <div class="address-system-actions">
    <the-button
      v-if="paramsOfButton.visibleAddAddress"
      block
      variant="tertiary"
      @click="clickToAddNewAddress()"
    >
      Добавить адрес
    </the-button>

    <the-button
      v-if="paramsOfButton.visibleShowMap"
      block
      variant="tertiary"
      @click="changeDialogContent(STATE_CONTENT_ADDRESS_SYSTEM.NEW)"
    >
      Посмотреть на карте
    </the-button>

    <the-button
      v-if="menuState !== STATE_MENU_ADDRESS_SYSTEM.NOT_DELIVERY"
      block
      size="md"
      :width="paramsOfButton.width"
      :loading="isLoadingWidget"
      :disabled="isDisabledAction"
      @click="onChangeBasketAddress"
      @keyup.enter="onChangeBasketAddress"
    >
      {{ paramsOfButton.title }}
    </the-button>
  </div>
</template>

<style scoped lang="scss">
.address-system-actions {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  justify-content: center;
}
</style>
